import { useState } from "react";
import "../styles/CourseRegistrationForm.css";

function CourseRegistrationForm({ onClose, courseId, courseName }) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        educationStatus: "",
        vrExperience: "",
        professionalLevel: "",
        birthday: "",
        city: "",
        hasVrGlasses: "",
        motivation: "",
        profession: "",
        monthlyIncome: ""
    });

    const [isFormValid, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, "");
        const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
            const part1 = match[1];
            const part2 = match[2];
            const part3 = match[3];
            if (part3) {
                return `${part1} ${part2} ${part3}`.trim();
            } else if (part2) {
                return `${part1} ${part2}`.trim();
            } else {
                return part1;
            }
        }
        return value;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === "phone") {
            formattedValue = formatPhoneNumber(value);
        }

        setFormData(prev => ({
            ...prev,
            [name]: formattedValue
        }));

        // Form validation
        validateForm({ ...formData, [name]: formattedValue });
    };

    const validateForm = (data) => {
        const errors = {};
        const requiredFields = ['name', 'email', 'phone', 'educationStatus', 'profession', 'monthlyIncome'];

        requiredFields.forEach(field => {
            if (!data[field] || data[field].trim() === '') {
                errors[field] = 'Bu alan zorunludur';
            }
        });

        // Email validation
        if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
            errors.email = 'Geçerli bir email adresi giriniz';
        }

        // Phone validation
        if (data.phone && data.phone.replace(/\s/g, '').length < 10) {
            errors.phone = 'Geçerli bir telefon numarası giriniz';
        }

        setFormErrors(errors);
        setIsFormValid(Object.keys(errors).length === 0 && requiredFields.every(field => data[field]));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid) return;

        setIsSubmitting(true);

        try {
            // Boolean değerleri düzgün formata çevir
            const convertToBoolean = (value) => {
                if (value === "evet") return true;
                if (value === "hayir") return false;
                return null; // Seçim yapılmamışsa null gönder
            };

            // Backend'e gönderilecek veri formatı
            const registrationData = {
                courseId: courseId,
                fullName: formData.name,
                email: formData.email,
                phone: formData.phone,
                birthday: formData.birthday || null,
                city: formData.city || null,
                profession: formData.profession,
                educationBackground: formData.educationStatus,
                monthlyIncome: formData.monthlyIncome,
                vrExperience: formData.vrExperience || null,
                hasVrDevice: convertToBoolean(formData.hasVrGlasses),
                professionalLevel: formData.professionalLevel || null,
                motivation: formData.motivation || null
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/course-participants/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(registrationData)
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Kayıt başarılı:', result);
                alert('🎉 Kayıt başarıyla tamamlandı! Kurs bilgileri e-posta adresinize gönderilecektir.');
                onClose();

                // Sayfa yenilendiğinde kayıt durumu güncellensin
                window.location.reload();
            } else {
                // Hata durumunda backend'den gelen mesajı göster
                const errorMessage = await response.text();
                console.error('Kayıt hatası:', errorMessage);
                alert(`❌ Kayıt sırasında bir hata oluştu: ${errorMessage}`);
            }

        } catch (error) {
            console.error('Network hatası:', error);
            alert('🔌 Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="registration-modal-overlay" onClick={onClose}>
            <div className="registration-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>🎓 Kursa Kayıt Ol</h2>
                    <p className="course-name">{courseName}</p>
                    <button className="close-button" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                    <div className="form-grid">
                        {/* Ad Soyad */}
                        <div className="form-group">
                            <label htmlFor="name">Ad Soyad *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Adınız ve soyadınız"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        {/* Email */}
                        <div className="form-group">
                            <label htmlFor="email">E-posta *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="ornek@email.com"
                                className={formErrors.email ? 'error' : ''}
                            />
                            {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                        </div>

                        {/* Telefon */}
                        <div className="form-group">
                            <label htmlFor="phone">Telefon *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="555 123 4567"
                                className={formErrors.phone ? 'error' : ''}
                            />
                            {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                        </div>

                        {/* Doğum Tarihi */}
                        <div className="form-group">
                            <label htmlFor="birthday">Doğum Tarihi</label>
                            <input
                                type="date"
                                id="birthday"
                                name="birthday"
                                value={formData.birthday}
                                onChange={handleInputChange}
                            />
                        </div>

                        {/* Şehir */}
                        <div className="form-group">
                            <label htmlFor="city">Şehir</label>
                            <input
                                type="text"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                placeholder="Yaşadığınız şehir"
                            />
                        </div>

                        {/* Meslek */}
                        <div className="form-group">
                            <label htmlFor="profession">Meslek *</label>
                            <input
                                type="text"
                                id="profession"
                                name="profession"
                                value={formData.profession}
                                onChange={handleInputChange}
                                placeholder="Mesleğiniz"
                                className={formErrors.profession ? 'error' : ''}
                            />
                            {formErrors.profession && <span className="error-message">{formErrors.profession}</span>}
                        </div>

                        {/* Eğitim Durumu */}
                        <div className="form-group">
                            <label htmlFor="educationStatus">Eğitim Durumu *</label>
                            <select
                                id="educationStatus"
                                name="educationStatus"
                                value={formData.educationStatus}
                                onChange={handleInputChange}
                                className={formErrors.educationStatus ? 'error' : ''}
                            >
                                <option value="">Seçiniz</option>
                                <option value="ilkokul">İlkokul</option>
                                <option value="ortaokul">Ortaokul</option>
                                <option value="lise">Lise</option>
                                <option value="onlisans">Ön Lisans</option>
                                <option value="lisans">Lisans</option>
                                <option value="yukseklisans">Yüksek Lisans</option>
                                <option value="doktora">Doktora</option>
                            </select>
                            {formErrors.educationStatus && <span className="error-message">{formErrors.educationStatus}</span>}
                        </div>

                        {/* Aylık Gelir */}
                        <div className="form-group">
                            <label htmlFor="monthlyIncome">Aylık Gelir Durumu *</label>
                            <select
                                id="monthlyIncome"
                                name="monthlyIncome"
                                value={formData.monthlyIncome}
                                onChange={handleInputChange}
                                className={formErrors.monthlyIncome ? 'error' : ''}
                            >
                                <option value="">Seçiniz</option>
                                <option value="asgari-alti">Asgari Ücretin Altında</option>
                                <option value="asgari-ustu">Asgari Ücretin Üstünde</option>
                                <option value="burslu-ogrenci">Burslu Öğrenci</option>
                            </select>
                            {formErrors.monthlyIncome && <span className="error-message">{formErrors.monthlyIncome}</span>}
                        </div>

                        {/* VR Deneyimi */}
                        <div className="form-group">
                            <label htmlFor="vrExperience">VR Deneyimi</label>
                            <select
                                id="vrExperience"
                                name="vrExperience"
                                value={formData.vrExperience}
                                onChange={handleInputChange}
                            >
                                <option value="">Seçiniz</option>
                                <option value="hic-yok">Hiç Yok</option>
                                <option value="az">Az</option>
                                <option value="orta">Orta</option>
                                <option value="iyi">İyi</option>
                                <option value="ileri">İleri Seviye</option>
                            </select>
                        </div>

                        {/* VR Gözlük */}
                        <div className="form-group">
                            <label htmlFor="hasVrGlasses">VR Gözlüğünüz var mı?</label>
                            <select
                                id="hasVrGlasses"
                                name="hasVrGlasses"
                                value={formData.hasVrGlasses}
                                onChange={handleInputChange}
                            >
                                <option value="">Seçiniz</option>
                                <option value="evet">Evet</option>
                                <option value="hayir">Hayır</option>
                                <option value="satin-almak-istiyorum">Satın almak istiyorum</option>
                            </select>
                        </div>

                        {/* Profesyonel Seviye */}
                        <div className="form-group">
                            <label htmlFor="professionalLevel">Profesyonel Seviye</label>
                            <select
                                id="professionalLevel"
                                name="professionalLevel"
                                value={formData.professionalLevel}
                                onChange={handleInputChange}
                            >
                                <option value="">Seçiniz</option>
                                <option value="baslangic">Başlangıç</option>
                                <option value="orta">Orta</option>
                                <option value="ileri">İleri</option>
                                <option value="uzman">Uzman</option>
                            </select>
                        </div>

                        {/* Motivasyon - Full width */}
                        <div className="form-group full-width">
                            <label htmlFor="motivation">Kursa katılma motivasyonunuz</label>
                            <textarea
                                id="motivation"
                                name="motivation"
                                value={formData.motivation}
                                onChange={handleInputChange}
                                placeholder="Bu kursa neden katılmak istiyorsunuz?"
                                rows="4"
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={onClose}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            className={`submit-btn ${!isFormValid ? 'disabled' : ''}`}
                            disabled={!isFormValid || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Kaydediliyor...
                                </>
                            ) : (
                                '✓ Kayıt Ol'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CourseRegistrationForm;
